// Pure helpers for ID-token debugging and Firebase auth-error classification.
// Kept separate from `api.ts` so they can be unit-tested without dragging
// Firebase init into the test runtime.

/**
 * Firebase **client** SDK auth-error codes that mean the session is
 * permanently invalid and the user needs to sign in again. Anything else
 * (network blip, IdP transient 5xx, fetch abort) is a recoverable failure
 * and must NOT trigger sign-out — that was the bug behind "kicked to /login
 * after long idle".
 *
 * `auth/user-token-expired` covers both natural expiry AND server-side token
 * revocation (the client SDK maps both REST conditions to this single code).
 *
 * `auth/no-current-user` is intentionally NOT terminal — it just means there
 * is no signed-in user, which is the expected state for anonymous visitors;
 * we handle that case at the call site by returning the original 401 rather
 * than redirecting an unauthenticated visitor to /login.
 *
 * `auth/id-token-revoked` is intentionally NOT here — that code is only
 * raised by `firebase-admin` (server SDK), never by the client SDK.
 */
export const TERMINAL_AUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  "auth/user-disabled",
  "auth/user-token-expired",
  "auth/user-not-found",
  "auth/invalid-user-token",
  "auth/requires-recent-login",
]);

/** Synthetic code thrown when the SDK is rehydrated but no user is set. */
export const NO_CURRENT_USER_CODE = "auth/no-current-user";

export function getErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

/**
 * Decode a JWT's `exp` claim (in ms) without verification. Used purely for
 * scheduling — we never trust this for authorization.
 */
export function readExpiryMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: number;
    };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTerminalAuthError(err: unknown): boolean {
  const code = getErrorCode(err);
  return code !== undefined && TERMINAL_AUTH_ERROR_CODES.has(code);
}

export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}
